import { useState, useEffect, useMemo } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Search, Check } from 'lucide-react';
import { cn } from '../lib/utils';

interface GroupPickerProps {
  /** Controlled open state */
  open: boolean;
  /** Controlled open toggle */
  onOpenChange: (open: boolean) => void;
  /** Currently selected group IDs (strings) */
  selectedGroups: string[];
  /** Called on "Confirm Selection" with updated group IDs */
  onSelectionChange: (groupIds: string[]) => void;
  /** Group ID → name mapping */
  groupMap: Record<string, string>;
  /** Loading state while fetching group map */
  isLoading?: boolean;
}

/**
 * GroupPicker — Radix Dialog modal for selecting forum groups.
 *
 * Follows the GroupPicker visual contract (UI-SPEC.md §2):
 * - Search input filters groupMap by name and ID (case-insensitive)
 * - Checkbox list with "Name (ID)" format per item
 * - Draft selection resets to selectedGroups when modal opens
 * - Footer with selected count, Discard Changes cancel, Confirm Selection confirm
 * - Loading state: 6 skeleton rows; empty search: "No groups match your search"
 */
export function GroupPicker({
  open,
  onOpenChange,
  selectedGroups,
  onSelectionChange,
  groupMap,
  isLoading = false,
}: GroupPickerProps) {
  // Draft selection — initialized from selectedGroups when modal opens
  const [draftSelection, setDraftSelection] = useState<string[]>([]);
  const [search, setSearch] = useState('');

  // Reset draft when modal opens (per UI-SPEC Pattern 3)
  useEffect(() => {
    if (open) {
      setDraftSelection([...selectedGroups]);
      setSearch('');
    }
  }, [open, selectedGroups]);

  // Filter groupMap by search (case-insensitive on name + ID)
  const filteredEntries = useMemo(() => {
    const needle = search.toLowerCase().trim();
    const entries = Object.entries(groupMap);
    if (!needle) return entries;
    return entries.filter(
      ([id, name]) =>
        name.toLowerCase().includes(needle) || id.includes(needle),
    );
  }, [groupMap, search]);

  const toggleGroup = (groupId: string) => {
    setDraftSelection((prev) =>
      prev.includes(groupId)
        ? prev.filter((g) => g !== groupId)
        : [...prev, groupId],
    );
  };

  const handleConfirm = () => {
    onSelectionChange(draftSelection);
    onOpenChange(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        {/* Overlay */}
        <Dialog.Overlay className="fixed inset-0 bg-bg/80" />

        {/* Modal content.
            aria-labelledby / aria-describedby are intentionally NOT set here:
            Radix auto-wires them to the generated ids of Dialog.Title and
            Dialog.Description below. Hard-coding an id on Dialog.Title (and
            mirroring it here) overrides Radix's generated titleId, so its
            runtime accessibility check (getElementById(generatedId)) fails and
            logs "DialogContent requires a DialogTitle". Letting Radix own the
            wiring keeps the markup accessible AND silences the warning. */}
        <Dialog.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-bg-1 border border-line rounded p-0 max-w-md w-full max-h-[85vh] flex flex-col"
        >
          {/* Header */}
          <div className="px-5 pt-5 pb-3 border-b border-line-soft relative">
            <Dialog.Title className="font-mono text-[13px] tracking-label uppercase text-ink">
              Select Forum Groups
            </Dialog.Title>
            {/* Visually hidden description: satisfies Radix's aria-describedby
                wiring (silences the "Missing Description" warning) and gives
                screen-reader users context the sighted UI conveys via layout. */}
            <Dialog.Description className="sr-only">
              Search and select the forum groups allowed to access this client.
            </Dialog.Description>
            <Dialog.Close
              aria-label="Close"
              className="absolute top-4 right-4 text-ink-3 hover:text-ink"
            >
              <X size={18} aria-hidden="true" />
            </Dialog.Close>
          </div>

          {/* Search input */}
          <div className="px-5 py-3">
            <div className="relative">
              <Search
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-3 pointer-events-none"
              />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search groups..."
                aria-label="Search groups"
                className="eo-input w-full bg-bg-2 border border-line text-ink px-2.5 py-1.5 font-mono text-[13px] rounded pl-8 focus:border-gold"
              />
            </div>
          </div>

          {/* Group list */}
          <div className="px-2 py-2 max-h-64 overflow-y-auto flex-1">
            {isLoading ? (
              /* Loading: 6 skeleton rows */
              Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="h-8 bg-bg-2 rounded animate-pulse mb-1"
                />
              ))
            ) : filteredEntries.length === 0 ? (
              /* Empty search results */
              <p className="text-[13px] text-ink-4 italic py-4 text-center">
                No groups match your search
              </p>
            ) : (
              filteredEntries.map(([id, name]) => {
                const isSelected = draftSelection.includes(id);
                return (
                  <label
                    key={id}
                    className={cn(
                      'flex items-center gap-2 px-3 py-2 rounded cursor-pointer hover:bg-bg-2 transition',
                      isSelected && 'bg-gold/5',
                    )}
                  >
                    {/* Custom checkbox */}
                    <span
                      className={cn(
                        'flex items-center justify-center w-4 h-4 border rounded flex-shrink-0',
                        isSelected
                          ? 'bg-gold border-gold'
                          : 'border-line',
                      )}
                    >
                      {isSelected && <Check size={12} className="text-[#1a1207]" />}
                    </span>
                    {/* Hidden native checkbox for a11y */}
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleGroup(id)}
                      className="sr-only"
                    />
                    <span className="text-[13px] font-mono text-ink-2 select-none">
                      {name} ({id})
                    </span>
                  </label>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="px-5 py-3 border-t border-line-soft flex items-center justify-between">
            <span className="font-mono text-[11px] text-ink-3">
              {draftSelection.length} group{draftSelection.length !== 1 ? 's' : ''}{' '}
              selected
            </span>
            <div className="flex gap-2">
              <Dialog.Close className="eo-btn eo-btn--ghost">
                Discard Changes
              </Dialog.Close>
              <button
                type="button"
                onClick={handleConfirm}
                className="eo-btn bg-gold text-[#1a1207] border-gold hover:bg-gold-hover"
              >
                Confirm Selection
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
